/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { isFunction } from '~libs.typescript';

import { Dispatch, SetStateAction, useLayoutEffect, useRef, useState } from '../../react';
import { useEffectOnce } from '../lifecycle/useEffectOnce';

type GlobalStateInitialSetter<STATE> = () => STATE;
type GlobalStateSetter<STATE> = (previousState: STATE) => STATE;
type GlobalInitialStateSetter<STATE> = STATE | GlobalStateInitialSetter<STATE>;
type GlobalResolvableState<STATE> = STATE | GlobalStateSetter<STATE>;
type GlobalStateDispatch<STATE> = (nextState: GlobalResolvableState<STATE>) => STATE;
type GlobalStateReturnFn<STATE> = [STATE, GlobalStateDispatch<STATE>];
type GlobalStore<STATE> = {
   state: STATE;
   setState: GlobalStateDispatch<STATE>;
   setters: Dispatch<SetStateAction<STATE>>[];
};

const resolveGlobalState = <STATE>(nextState: GlobalResolvableState<STATE>, currentState?: STATE): STATE => {
   if (isFunction(nextState)) {
      const setter = nextState as (previousState?: STATE) => STATE;
      return nextState.length ? setter(currentState) : setter();
   }

   return nextState;
};

export const createGlobalState = <STATE>(initialState: GlobalInitialStateSetter<STATE>) => {
   let cachedStore: GlobalStore<STATE> | null = null;
   const createStore = (): GlobalStore<STATE> => {
      if (!cachedStore) {
         const newStore: GlobalStore<STATE> = {
            state: initialState instanceof Function ? initialState() : initialState,
            setState: (nextState) => {
               newStore.state = resolveGlobalState(nextState, newStore.state);
               newStore.setters.forEach((setter) => setter(newStore.state));
               return newStore.state;
            },
            setters: [],
         };
         cachedStore = newStore;
      }

      return cachedStore;
   };

   return (): GlobalStateReturnFn<STATE> => {
      const { current: store } = useRef(createStore());
      const [globalState, stateSetter] = useState<STATE>(store.state);

      useEffectOnce(() => () => {
         store.setters = store.setters.filter((setter) => setter !== stateSetter);
      });

      useLayoutEffect(() => {
         if (!store.setters.includes(stateSetter)) {
            store.setters.push(stateSetter);
         }
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      return [globalState, store.setState];
   };
};
